// "Where it's used" — the read-only twin of DeleteObjectDialog's dependency
// check. Both ask the SAME backend question (/designers/object-dependents):
// which other objects reference this one. The delete dialog uses the answer
// to BLOCK; this one just shows it, so a card's ⋮ menu can answer "what would
// break if I changed this?" without going near the delete path.
// `DependentList` is the shared row rendering used by both dialogs.
import { useEffect, useState } from 'react'
import { Link2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import {
  objectDependents,
  type DependableType,
  type ObjectDependent,
} from '@/api/designers'
import { MicroBadge, TYPE_COLORS, TypeChip, errMessage } from './shared'

/** Leading marker for one dependent row — the real object types get their
    identity chip; cross-surface refs (menu / assistant) get a small badge. */
function DependentMarker({ dep }: { dep: ObjectDependent }) {
  if (TYPE_COLORS[dep.ref_type]) {
    return <TypeChip code={dep.ref_type} size={22} />
  }
  return <MicroBadge text={dep.ref_type_label} tone="accent" />
}

/** Scrollable list of "this is referenced by …" rows. */
export function DependentList({ deps }: { deps: ObjectDependent[] }) {
  return (
    <div style={{ display: 'grid', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
      {deps.map((d, i) => (
        <div
          key={`${d.ref_type}:${d.ref_name}:${i}`}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 11px', borderRadius: 9,
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}
        >
          <DependentMarker dep={d} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{ fontSize: 14.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={d.ref_name}
            >
              {d.ref_name}
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>
              {d.ref_type_label} · {d.relation}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function WhereUsedDialog({
  open,
  project,
  objType,
  objName,
  objLabel,
  displayName,
  onClose,
}: {
  open: boolean
  project: string
  objType: DependableType
  objName: string
  /** Lowercase noun for prose, e.g. "visual", "dashboard". */
  objLabel: string
  /** Friendly title to show (defaults to objName). */
  displayName?: string
  onClose: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [deps, setDeps] = useState<ObjectDependent[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setDeps(null)
    objectDependents(project, objType, objName)
      .then((r) => { if (!cancelled) setDeps(r.dependents || []) })
      .catch((e) => { if (!cancelled) setError(errMessage(e, 'Could not check where this is used')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, project, objType, objName])

  const title = displayName || objName

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Where “${title}” is used`}
      footer={<button className="btn btn-secondary" onClick={onClose}>Close</button>}
    >
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, color: 'var(--color-text-secondary)', padding: '6px 2px' }}>
          <div className="spinner" style={{ width: 15, height: 15 }} />
          Checking where “{title}” is used…
        </div>
      ) : error ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
            borderRadius: 8, border: '1px solid var(--color-error)',
            background: 'var(--color-error-weak)', color: 'var(--color-error)', fontSize: 15,
          }}
        >
          {error} — try again.
        </div>
      ) : (deps?.length ?? 0) === 0 ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, lineHeight: 1.55 }}>
          <Link2 size={18} style={{ color: 'var(--color-text-secondary)', flexShrink: 0, marginTop: 1 }} />
          <div>
            Nothing references this {objLabel} yet — it isn’t on a dashboard, a
            menu, a page or an assistant. Changing or deleting it affects nothing else.
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 15, lineHeight: 1.55 }}>
            <strong>“{title}”</strong> is used in {deps!.length} place{deps!.length === 1 ? '' : 's'}.
            Changing it changes {deps!.length === 1 ? 'this' : 'these'} too.
          </div>
          <DependentList deps={deps!} />
        </div>
      )}
    </Modal>
  )
}
